import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authService } from '../services/auth/authService';
import { STORAGE_KEYS, HTTP_STATUS, BASE_PATH } from '../utils/constants';

// Decode base64url (JWT uses base64url, not standard base64)
const base64UrlDecode = (str) => {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  return atob(base64);
};

// Role normalization helper (extracted to avoid duplication)
const normalizeRole = (rawRole) => {
  const r = (rawRole || '').toString().toLowerCase().trim();
  if (r.includes('superadmin') || r.includes('super_admin') || r.includes('super admin')) return 'superadmin';
  if (r.includes('admin')) return 'admin';
  if (r.includes('doctor') || r.includes('physician')) return 'doctor';
  if (r.includes('support') || r.includes('staff') || r.includes('employee')) return 'staff';
  if (r.includes('patient')) return 'patient';
  return r;
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const tokenExpiryTimerRef = useRef(null);
  const initRef = useRef(false);

  // Clear session data
  const clearSession = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSessionExpired(false);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }
  }, []);

  // Auto-logout on token expiry
  const setupTokenExpiryCheck = useCallback(() => {
    if (tokenExpiryTimerRef.current) {
      clearTimeout(tokenExpiryTimerRef.current);
      tokenExpiryTimerRef.current = null;
    }

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return;

    try {
      const tokenPayload = JSON.parse(base64UrlDecode(token.split('.')[1]));
      if (typeof tokenPayload.exp !== 'number') return;

      const timeUntilExpiry = tokenPayload.exp * 1000 - Date.now();

      if (timeUntilExpiry > 0) {
        tokenExpiryTimerRef.current = setTimeout(() => {
          setSessionExpired(true);
        }, timeUntilExpiry);
      } else {
        setSessionExpired(true);
      }
    } catch (error) {
      console.error('Token expiry check error:', error);
      setSessionExpired(true);
    }
  }, []);

  // Token validation and session management
  const validateSession = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);

    if (!token || !savedUser) {
      return false;
    }

    try {
      const tokenPayload = JSON.parse(base64UrlDecode(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      if (typeof tokenPayload.exp === 'number' && tokenPayload.exp < currentTime) {
        await clearSession();
        setSessionExpired(true);
        return false;
      }

      const result = await authService.verifyToken();
      if (result.success) {
        const userData = JSON.parse(savedUser);
        userData.role = normalizeRole(userData.role || userData.role_name);

        if (result.data?.user) {
          userData.admin_id = result.data.user.admin_id || userData.admin_id;
          userData.hospital_code = result.data.user.hospital_code || userData.hospital_code;
          userData.hospital_name = result.data.user.hospital_name || userData.hospital_name;

          if (Array.isArray(result.data.user.access_modules)) {
            userData.accessModules = result.data.user.access_modules;
            userData.access_modules = result.data.user.access_modules;
          }

          // Sync doctor photo from verify response
          if (result.data.user.photo !== undefined) {
            userData.photo = result.data.user.photo;
          }
        }

        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        setUser(userData);
        return true;
      } else {
        await clearSession();
        setSessionExpired(true);
        return false;
      }
    } catch (error) {
      console.error('Session validation error:', error);
      await clearSession();
      setSessionExpired(true);
      return false;
    }
  }, [clearSession]);

  // Initialize auth ONCE (useRef guard prevents StrictMode double-fire)
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initializeAuth = async () => {
      setLoading(true);
      const isValidSession = await validateSession();
      if (isValidSession) {
        setupTokenExpiryCheck();
      }
      setLoading(false);
    };

    initializeAuth();
  }, [validateSession, setupTokenExpiryCheck]);

  const login = useCallback(
    async (mobileNumber, password, rememberMe = false) => {
      try {
        const result = await authService.login(mobileNumber, password);

        if (result.success) {
          const userData = result.data.user;
          userData.token = result.data.token;
          userData.role = normalizeRole(userData.role || userData.role_name);

          if (Array.isArray(userData.access_modules)) {
            userData.accessModules = userData.access_modules;
          }

          setUser(userData);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
          localStorage.setItem(STORAGE_KEYS.TOKEN, result.data.token);

          if (rememberMe) {
            localStorage.setItem(
              STORAGE_KEYS.REMEMBER_CREDENTIALS,
              JSON.stringify({
                mobileNumber,
                password,
              })
            );
          } else {
            localStorage.removeItem(STORAGE_KEYS.REMEMBER_CREDENTIALS);
          }

          setupTokenExpiryCheck();
          return userData;
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },
    [setupTokenExpiryCheck]
  );

  const loginDirect = useCallback(
    (userData, token) => {
      userData.role = normalizeRole(userData.role || userData.role_name);
      userData.token = token;

      if (Array.isArray(userData.access_modules)) {
        userData.accessModules = userData.access_modules;
      }

      setUser(userData);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      setupTokenExpiryCheck();

      return userData;
    },
    [setupTokenExpiryCheck]
  );

  const impersonateAdmin = useCallback(
    (adminUserData, adminToken) => {
      const currentUser = localStorage.getItem(STORAGE_KEYS.USER);
      const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      sessionStorage.setItem(
        STORAGE_KEYS.SUPERADMIN_SESSION,
        JSON.stringify({
          user: currentUser,
          token: currentToken,
        })
      );
      return loginDirect(adminUserData, adminToken);
    },
    [loginDirect]
  );

  const returnToSuperAdmin = useCallback(() => {
    const saved = sessionStorage.getItem(STORAGE_KEYS.SUPERADMIN_SESSION);
    if (!saved) return false;

    try {
      const { user: savedUser, token: savedToken } = JSON.parse(saved);
      sessionStorage.removeItem(STORAGE_KEYS.SUPERADMIN_SESSION);

      localStorage.setItem(STORAGE_KEYS.USER, savedUser);
      localStorage.setItem(STORAGE_KEYS.TOKEN, savedToken);
      setUser(JSON.parse(savedUser));
      setupTokenExpiryCheck();
      return true;
    } catch (error) {
      console.error('Error returning to SuperAdmin:', error);
      sessionStorage.removeItem(STORAGE_KEYS.SUPERADMIN_SESSION);
      return false;
    }
  }, [setupTokenExpiryCheck]);

  const signup = useCallback(async (userData) => {
    try {
      const result = await authService.register(userData);
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }, []);

  const checkMobileAndProceed = useCallback(async (mobileNumber) => {
    try {
      const result = await authService.checkMobile(mobileNumber);
      return result;
    } catch (error) {
      console.error('Check mobile error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await clearSession();
      window.location.href = `${BASE_PATH}/login`;
    } catch (error) {
      console.error('Logout error:', error);
      await clearSession();
      window.location.href = `${BASE_PATH}/login`;
    }
  }, [clearSession]);

  const getRememberedCredentials = useCallback(() => {
    try {
      const remembered = localStorage.getItem(STORAGE_KEYS.REMEMBER_CREDENTIALS);
      return remembered ? JSON.parse(remembered) : null;
    } catch (error) {
      console.error('Error getting remembered credentials:', error);
      return null;
    }
  }, []);

  const clearRememberedCredentials = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_CREDENTIALS);
    } catch (error) {
      console.error('Error clearing remembered credentials:', error);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const result = await authService.refreshToken();
      if (result.success && result.data?.token) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, result.data.token);

        if (result.data.user) {
          const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
          const userData = savedUser ? JSON.parse(savedUser) : {};

          userData.admin_id = result.data.user.admin_id || userData.admin_id;
          userData.hospital_code = result.data.user.hospital_code || userData.hospital_code;
          userData.hospital_name = result.data.user.hospital_name || userData.hospital_name;
          if (Array.isArray(result.data.user.access_modules)) {
            userData.accessModules = result.data.user.access_modules;
            userData.access_modules = result.data.user.access_modules;
          }

          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
          setUser(userData);
        }

        setSessionExpired(false);
        setupTokenExpiryCheck();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session refresh error:', error);
      return false;
    }
  }, [setupTokenExpiryCheck]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const hasModuleAccess = useCallback(
    (moduleSlug) => {
      if (!moduleSlug) return true;
      if (!user || user.role !== 'staff') return true;
      const modules = user.accessModules || user.access_modules;
      if (!modules || !Array.isArray(modules) || modules.length === 0) {
        return false;
      }
      return modules.includes(moduleSlug);
    },
    [user]
  );

  const isImpersonating = useMemo(() => {
    return !!sessionStorage.getItem(STORAGE_KEYS.SUPERADMIN_SESSION);
  }, [user]); // only recompute when user changes (impersonation changes user)

  // Memoize the entire context value to prevent cascading re-renders
  const updateUser = useCallback((fields) => {
    setUser((prev) => {
      const updated = { ...prev, ...fields };
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      login,
      loginDirect,
      signup,
      checkMobileAndProceed,
      logout,
      loading,
      sessionExpired,
      clearSession,
      clearSessionExpired,
      getRememberedCredentials,
      clearRememberedCredentials,
      refreshSession,
      validateSession,
      hasModuleAccess,
      isImpersonating,
      impersonateAdmin,
      returnToSuperAdmin,
      updateUser,
    }),
    [
      user,
      loading,
      sessionExpired,
      isImpersonating,
      login,
      loginDirect,
      signup,
      checkMobileAndProceed,
      logout,
      clearSession,
      clearSessionExpired,
      getRememberedCredentials,
      clearRememberedCredentials,
      refreshSession,
      validateSession,
      hasModuleAccess,
      impersonateAdmin,
      returnToSuperAdmin,
      updateUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
